import React, { FC }        from 'react';
import styled               from 'styled-components';
import { Fab, Button }      from '@material-ui/core';
import { Edit, Delete }     from '@material-ui/icons';
import { ICardAd }          from '@typings/nationalAdManagement';
import { EAdType }          from '@enums/nationalAdManagement';
import { checkUrlProtocol } from '@utils/commonFunctions';
import CUALogo              from '../assets/cualogo.png';
import { AdDetails }        from './AdDetails';

interface IStyledProps {
  textOnly?   : boolean;
  imageUrl?   : string;
  withButton? : boolean;
}

const Wrapper = styled.div`
  display        : flex;
  flex-direction : column;
  max-width      : 335px;
  margin         : 0 auto;
  border-radius  : 5px;
  box-shadow     : 1px 1px 15px #AAA;
`;

const Actions = styled.div`
  display  : none;
  position : absolute;
  top      : 8px;
  right    : 8px;

  button {
    height           : 36px;
    margin-left      : 5px;
    padding          : 0 10px 0 18px !important;
    background-color : ${({ theme: { backgroundColor } }): string => backgroundColor.white};

    span {
      line-height : normal;
    }
  }

  svg {
    margin-left : 4px;
  }
`;

const AdContainer = styled.div<IStyledProps>`
  display             : flex;
  flex-direction      : column;
  justify-content     : flex-end;
  position            : relative;
  border-radius       : 5px;
  width               : 100%;
  height              : 382px;
  background-repeat   : no-repeat;
  background-color    : ${({ theme: { backgroundColor } }): string => backgroundColor.darkBlue};
  background-image    : url(${({ textOnly, imageUrl }): string => ((textOnly || !imageUrl) ? CUALogo : imageUrl)});
  background-position : ${({ textOnly, imageUrl }): string => ((textOnly || !imageUrl) ? '50% 22%' : 'center')};
  background-size     : ${({ textOnly, imageUrl }): string => ((textOnly || !imageUrl) ? '85% auto' : 'cover')};

  :hover {
    ${Actions} {
      display : block;
    }
  }
`;

const DescriptionWrapper = styled.div<IStyledProps>`
  width         : 100%;
  height        : 165px;
  position      : absolute;
  background    : #3B3B3B 0 0 no-repeat padding-box;
  border-radius : 0 0 5px 5px;
  opacity       : 0.89;
  padding       : 8px 15px 15px;
  color         : ${({ theme: { textColor } }): string => textColor.white};
  bottom        : 0;

  p {
    font-size     : 18px;
    font-weight   : 500;
    margin-bottom : 6px;
  }

  span {
    display        : block;
    font-size      : 14px;
    font-weight    : 200;
    line-height    : 20px;
    letter-spacing : 0.28px;
    max-height     : ${({ withButton }): string => (withButton ? '60px' : '120px')};
    overflow       : hidden;
    word-break     : break-word;
  }
`;

const ActionButton = styled(Button)`
  && {
    height         : 44px;
    margin         : 15px;
    padding        : 8px 15px;
    letter-spacing : 0.32px;

    span {
      font-size     : 16px;
      font-weight   : 500;
      width         : 300px;
      display       : block;
      white-space   : nowrap;
      overflow      : hidden;
      text-overflow : ellipsis;
    }
  }
`;

interface IAdItemProps {
  withActions? : boolean;
  withDetails? : boolean;
  adData       : Partial<ICardAd>;
  logoUrl?     : string;
  onEditAd     : (id: string) => void;
  onDeleteAd   : (id: string) => void;
}

export const AdItem: FC<IAdItemProps> = ({
  adData,
  onEditAd,
  onDeleteAd,
  logoUrl,
  withActions = false,
  withDetails = false,
}) => (
  <Wrapper>
    <AdContainer
      imageUrl = {adData.imageUrl || logoUrl}
      textOnly = {adData.type === EAdType.TextOnlyAd}
    >
      {adData.type !== EAdType.GraphicOnlyAd && (
        <DescriptionWrapper withButton={adData.isClickable}>
          <p>{adData.title}</p>
          <span>{adData.body}</span>
        </DescriptionWrapper>
      )}

      {adData.isClickable && (
        <ActionButton
          variant = "contained"
          color   = "secondary"
          size    = "small"
          onClick = {() => {
            if (adData.link) {
              window.open(checkUrlProtocol(adData.link));
            }
          }}
        >
          {adData.buttonName}
        </ActionButton>
      )}

      {withActions && (
        <Actions>
          <Fab
            aria-label = "edit"
            size       = "small"
            variant    = "extended"
            onClick    = {(): void => onEditAd(adData.id || '')}
          >
            EDIT
            <Edit color = "secondary" />
          </Fab>

          <Fab
            aria-label = "delete"
            size       = "small"
            variant    = "extended"
            onClick    = {(): void => onDeleteAd(adData.id || '')}
          >
            DELETE
            <Delete color = "error" />
          </Fab>
        </Actions>
      )}
    </AdContainer>

    {withDetails && <AdDetails adData={adData} />}
  </Wrapper>
);
